import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { MagneticButton } from '@/components/site/MagneticButton';
import { SITE, SERVICES } from '@/data/site';
import { getLenis } from '@/hooks/useLenis';

export function DeltaMark({ className = '' }) {
  return (
    <svg viewBox="0 0 32 32" className={className} aria-hidden>
      <defs>
        <linearGradient id="delta-g" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0%" stopColor="#6C5CE7" />
          <stop offset="100%" stopColor="#4B9FFF" />
        </linearGradient>
      </defs>
      <path d="M16 5 L28 26 L4 26 Z" fill="none" stroke="url(#delta-g)" strokeWidth="2" strokeLinejoin="round" />
      <path d="M16 11.5 L22.5 23.5 L9.5 23.5 Z" fill="url(#delta-g)" opacity="0.9" />
    </svg>
  );
}

const NAV = [
  { to: '/', label: 'Home', end: true },
  { to: '/services/', label: 'Services', mega: true },
  { to: '/work/', label: 'Work' },
  { to: '/about/', label: 'About' },
  { to: '/website-development-cost/', label: 'Pricing' },
  { to: '/contact/', label: 'Contact' },
];

function StudioClock() {
  const [t, setT] = useState('--:--');
  useEffect(() => {
    const fmt = () => {
      try {
        setT(new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' }).format(new Date()));
      } catch (e) { /* older browsers: keep placeholder */ }
    };
    fmt();
    const id = setInterval(fmt, 15000);
    return () => clearInterval(id);
  }, []);
  return (
    <span className="nd-mono hidden xl:inline-flex items-center gap-2">
      <span className="nd-live" aria-hidden />
      <span>Studio <span className="tabular-nums">{t}</span> IST</span>
    </span>
  );
}

const linkCls = ({ isActive }) =>
  `group relative block px-3.5 py-2 text-sm transition-colors duration-200 ${isActive ? 'text-white' : 'text-ink-muted hover:text-white'}`;

function Underline() {
  return (
    <span
      aria-hidden
      className="absolute left-3.5 right-3.5 bottom-1 h-px bg-white origin-right scale-x-0 transition-transform duration-500 [transition-timing-function:var(--ease-out)] group-hover:origin-left group-hover:scale-x-100"
    />
  );
}

export function Header() {
  const [solid, setSolid] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const lastY = useRef(0);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setSolid(y > 40);
      setHidden(y > lastY.current && y > 400);
      lastY.current = y;
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setMegaOpen(false);
    setHidden(false);
  }, [location.pathname]);

  useEffect(() => {
    const lenis = getLenis();
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    if (lenis) (mobileOpen ? lenis.stop() : lenis.start());
    const onKey = (e) => { if (e.key === 'Escape') setMobileOpen(false); };
    if (mobileOpen) document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileOpen]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-50 border-b transition-[transform,background-color,border-color] duration-500 [transition-timing-function:var(--ease-out)] ${
          solid || mobileOpen ? 'bg-[#0A0A0F]/75 backdrop-blur-xl backdrop-saturate-150 border-white/[0.08]' : 'bg-transparent border-transparent'
        } ${hidden && !mobileOpen && !megaOpen ? '-translate-y-full' : 'translate-y-0'}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 h-[76px] flex items-center justify-between gap-6">
          <Link to="/" data-testid="header-logo" className="flex items-center gap-2.5 group" aria-label="NioDelta home">
            <DeltaMark className="h-7 w-7 transition-transform duration-700 [transition-timing-function:var(--ease-out)] group-hover:rotate-[120deg]" />
            <span className="font-display text-[17px] font-semibold tracking-[-0.02em]">
              Nio<span className="text-ink-muted">Delta</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-0.5" data-testid="header-nav" aria-label="Primary">
            {NAV.map((item) =>
              item.mega ? (
                <div
                  key={item.to}
                  className="relative"
                  onMouseEnter={() => setMegaOpen(true)}
                  onMouseLeave={() => setMegaOpen(false)}
                  onFocus={() => setMegaOpen(true)}
                  onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setMegaOpen(false); }}
                >
                  <NavLink to={item.to} data-testid={`nav-link-${item.label.toLowerCase()}`} className={linkCls} aria-haspopup="true" aria-expanded={megaOpen}>
                    {item.label}
                    <span className={`ml-1.5 inline-block text-[10px] opacity-60 transition-transform duration-300 ${megaOpen ? 'rotate-180' : ''}`}>▾</span>
                    <Underline />
                  </NavLink>
                  <div
                    data-testid="services-mega-menu"
                    className={`absolute left-1/2 top-full pt-3 w-[min(920px,90vw)] -translate-x-1/2 transition-[opacity,transform] duration-200 [transition-timing-function:var(--ease-out)] origin-top ${
                      megaOpen ? 'opacity-100 translate-y-0 visible' : 'opacity-0 -translate-y-1 invisible pointer-events-none'
                    }`}
                  >
                    <div className="rounded-[18px] border border-white/10 bg-[#0E0E15]/95 backdrop-blur-xl p-3 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.9)]">
                      <div className="grid grid-cols-2 gap-1">
                        {SERVICES.map((s) => (
                          <Link
                            key={s.slug}
                            to={`/services/${s.slug}/`}
                            data-testid={`mega-service-${s.slug}`}
                            className="arrow-link group/item rounded-xl px-4 py-4 hover:bg-white/[0.04] transition-colors"
                          >
                            <div className="flex items-baseline justify-between gap-4">
                              <div className="flex items-baseline gap-3">
                                <span className="nd-mono !text-[11px]">{s.num}</span>
                                <span className="font-display text-[17px] font-semibold tracking-[-0.02em] text-white">{s.title}</span>
                              </div>
                              <span className="arrow text-ink-muted group-hover/item:text-white">→</span>
                            </div>
                            <p className="mt-1.5 pl-8 text-[13px] text-ink-muted leading-snug">{s.short}</p>
                          </Link>
                        ))}
                      </div>
                      <div className="mt-2 px-4 py-4 rounded-xl bg-white/[0.03] flex items-center justify-between">
                        <span className="text-sm text-ink-muted">Not sure where you fit?</span>
                        <Link to="/contact/" className="nd-link-u text-sm text-white">Tell us the problem →</Link>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <NavLink key={item.to} to={item.to} end={item.end} data-testid={`nav-link-${item.label.toLowerCase()}`} className={linkCls}>
                  {item.label}
                  <Underline />
                </NavLink>
              )
            )}
          </nav>

          <div className="flex items-center gap-5">
            <StudioClock />
            <div className="hidden sm:block">
              <MagneticButton href={SITE.whatsapp} external variant="primary" size="sm" data-testid="header-cta-whatsapp">
                Book discovery call
              </MagneticButton>
            </div>
            <button
              type="button"
              className="lg:hidden relative h-11 w-11 rounded-full border border-white/15"
              onClick={() => setMobileOpen((s) => !s)}
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
              aria-controls="mobile-nav"
              data-testid="header-mobile-toggle"
            >
              <span className={`absolute left-[13px] right-[13px] top-[18px] h-[1.5px] bg-white transition-transform duration-500 [transition-timing-function:var(--ease-out)] ${mobileOpen ? 'translate-y-[3px] rotate-45' : ''}`} />
              <span className={`absolute left-[13px] right-[13px] top-[24px] h-[1.5px] bg-white transition-transform duration-500 [transition-timing-function:var(--ease-out)] ${mobileOpen ? '-translate-y-[3px] -rotate-45' : ''}`} />
            </button>
          </div>
        </div>
      </header>

      <div
        id="mobile-nav"
        data-testid="mobile-nav"
        aria-hidden={!mobileOpen}
        className={`lg:hidden fixed inset-0 z-40 bg-ink-bg px-4 sm:px-6 pt-[104px] pb-8 flex flex-col justify-between transition-[clip-path,visibility] duration-700 [transition-timing-function:var(--ease-in-out)] ${
          mobileOpen ? '[clip-path:inset(0_0_0_0)] visible' : '[clip-path:inset(0_0_100%_0)] invisible'
        }`}
      >
        <ul>
          {NAV.map((item, i) => (
            <li key={item.to} className="overflow-hidden border-b border-white/[0.08]">
              <NavLink
                to={item.to}
                end={item.end}
                tabIndex={mobileOpen ? 0 : -1}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `flex items-baseline justify-between py-3.5 font-display text-[clamp(36px,10vw,56px)] font-semibold tracking-[-0.04em] leading-[1.05] transition-transform [transition-duration:800ms] [transition-timing-function:var(--ease-out)] ${
                    isActive ? 'text-white' : 'text-white/60'
                  } ${mobileOpen ? 'translate-y-0' : 'translate-y-[105%]'}`
                }
                style={{ transitionDelay: mobileOpen ? `${120 + i * 50}ms` : '0ms' }}
              >
                {item.label}
                <span className="nd-mono">0{i}</span>
              </NavLink>
            </li>
          ))}
        </ul>
        <div className="flex flex-col gap-5">
          <MagneticButton href={SITE.whatsapp} external variant="primary" data-testid="mobile-cta-whatsapp" tabIndex={mobileOpen ? 0 : -1}>
            Book discovery call
          </MagneticButton>
          <a className="nd-mono" href={`mailto:${SITE.email}`} tabIndex={mobileOpen ? 0 : -1}>{SITE.email}</a>
        </div>
      </div>
    </>
  );
}
